import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { EntraSignInButton } from '../../auth/EntraSignInButton';
import { isEntraConfigured } from '../../auth/entraConfig';
import { SIGN_IN_ROLES, roleLabel } from '../../auth/signInRole';
import { workspaceHomePath } from '../../auth/workspaces';
import { UmbLogo } from '../../components/layout/UmbLogo';

const ROLE_REQUIRED_MESSAGE = 'Choose whether you are signing in as an Originator, Provider or Custodian.';

export function SignInPage() {
  const navigate = useNavigate();
  const { signIn, signInWithMicrosoft, selectOrganization, setAuthError, authError } =
    useAuth();
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [microsoftBusy, setMicrosoftBusy] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!role) {
      setAuthError(ROLE_REQUIRED_MESSAGE);
      return;
    }
    setBusy(true);
    setAuthError(null);
    try {
      const result = await signIn(email.trim(), password, role);
      const memberships = result.memberships || [];
      if (memberships.length === 1) {
        const next = await selectOrganization(memberships[0].organization_id);
        navigate(workspaceHomePath(next.workspaceId));
        return;
      }
      if (memberships.length === 0) {
        setAuthError(`This account has no ${roleLabel(role)} workspace.`);
        return;
      }
      navigate('/select-workspace');
    } catch (err) {
      setAuthError(err.message || 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleMicrosoft() {
    if (!role) {
      setAuthError(ROLE_REQUIRED_MESSAGE);
      return;
    }
    setMicrosoftBusy(true);
    setAuthError(null);
    try {
      await signInWithMicrosoft(role);
    } catch (err) {
      setAuthError(err.message || 'Microsoft sign-in failed');
      setMicrosoftBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-eorig-surface px-4 py-10 font-sans text-eorig-ink [word-spacing:0.06em]">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-eorig-border bg-white p-6 shadow-eorig-card sm:p-8">
          <div className="flex items-start justify-between gap-3">
            <UmbLogo />
            <span className="inline-flex items-center rounded-full border border-eorig-border bg-eorig-surface px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-normal text-eorig-muted">
              Secure sign-in
            </span>
          </div>
          <h1 className="mt-6 text-2xl font-semibold tracking-normal text-eorig-ink">
            Sign in to UMB eVault
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-eorig-muted">
            Use your organization Microsoft account or your email and password,
            and pick the role you are signing in as. Access is granted only to
            the workspaces your Provider admin has invited you to.
          </p>

          {isEntraConfigured() ? (
            <div className="mt-6 space-y-3">
              <EntraSignInButton busy={microsoftBusy} onClick={handleMicrosoft} />
              <p className="text-center text-[10px] font-semibold uppercase tracking-normal text-eorig-muted">
                Or sign in with email
              </p>
            </div>
          ) : null}

          <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
            <label className="block text-sm">
              <span className="mb-1.5 block font-medium text-eorig-ink">Email</span>
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-eorig-border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-eorig-cyan/35"
              />
            </label>
            <div className="text-sm">
              <div className="mb-1.5 flex items-center justify-between">
                <label htmlFor="signin-password" className="font-medium text-eorig-ink">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-eorig-cyan underline"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="signin-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-eorig-border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-eorig-cyan/35"
              />
            </div>
            <label className="block text-sm">
              <span className="mb-1.5 block font-medium text-eorig-ink">Sign in as</span>
              <select
                required
                value={role}
                onChange={(e) => {
                  setRole(e.target.value);
                  if (authError === ROLE_REQUIRED_MESSAGE) setAuthError(null);
                }}
                className="w-full rounded-xl border border-eorig-border bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-eorig-cyan/35"
              >
                <option value="">Select your role</option>
                {SIGN_IN_ROLES.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {authError ? (
              <p className="text-sm text-red-600" role="alert">
                {authError}
              </p>
            ) : null}
            <button
              type="submit"
              disabled={busy || microsoftBusy}
              className="w-full rounded-xl bg-umb-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95 disabled:opacity-60"
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-eorig-muted">
            Invited to a workspace?{' '}
            <Link to="/invite" className="font-medium text-eorig-cyan underline">
              Accept an invite
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
